import { ref, computed } from 'vue';

import { cloneDeep } from 'lodash';
import { defineStore } from 'pinia';

import api from '@/api/index';
import { useNotification } from '@/composables/shared/useNotification';
import { useAlertStore } from '@/stores/alert';
import { QRCodeType } from '@/stores/chat';
import { UserType } from '@/stores/user';
import { ChatOptions } from '@/types/components';
import { IResponseQrCode } from '@/types/response';
import { CHAT_QR_OPTIONS } from '@/utils/constants/chat';

export const useCallStore = defineStore(
  'call',
  () => {
    const alertStore = useAlertStore();

    const { alertLoginGuestAccess, alertEvictMyself } = useNotification();

    const qrCodeInfo = ref<IResponseQrCode>({
      title: '',
      qrCodeType: '',
      accessType: '',
      partnerId: 0,
      callType: '',
      hostCode: '',
      hostQrCodeImageUrl: '',
      hostLinkUrl: '',
      entranceLogoImageUrl: '',
      callLogoImageUrl: '',
      guestCode: '',
      guestQrCodeImageUrl: '',
      guestLinkUrl: '',
      partnerType: '',
      options: cloneDeep(CHAT_QR_OPTIONS),
    });
    const userInfo = ref({
      registeredDeviceId: null,
      userType: null, //
      isTester: false, //
      nickname: '',
    });
    const saveAudio = ref(false);

    const apecChatStatus = ref('NOT_STARTED');
    const apecChatStartedAt = ref('');

    const qrOptions = computed<ChatOptions>(() => {
      return { ...CHAT_QR_OPTIONS, ...qrCodeInfo.value.options };
    });

    const init = () => {
      qrCodeInfo.value = {
        code: '',
        title: '',
        qrCodeType: '',
        accessType: '',
        partnerId: 0,
        callType: '',
        hostCode: '',
        hostQrCodeImageUrl: '',
        hostLinkUrl: '',
        entranceLogoImageUrl: '',
        callLogoImageUrl: '',
        guestCode: '',
        guestQrCodeImageUrl: '',
        guestLinkUrl: '',
        partnerType: '',
        options: cloneDeep(CHAT_QR_OPTIONS),
      };
      userInfo.value = {
        registeredDeviceId: null,
        userType: null, //
        isTester: false, //
        nickname: '',
      };
      saveAudio.value = false;
    };

    function setQrCodeInfo(info) {
      qrCodeInfo.value = info;
      if (info.partnerCode) {
        localStorage.setItem('partner-code-admin', info.partnerCode);
      }
      normalizeBooleanStrings(qrCodeInfo.value.options);
    }

    function normalizeBooleanStrings(obj) {
      for (const key in obj) {
        if (obj[key] === 'true') obj[key] = true;
        else if (obj[key] === 'false') obj[key] = false;
      }
    }

    const updateCode = (newCode) => {
      qrCodeInfo.value.code = newCode;
    };

    const updateFairconInfo = (callFairconResponse) => {
      qrCodeInfo.value.hostQrCodeImageUrl = callFairconResponse.hostQrCodeImageUrl;
      qrCodeInfo.value.hostLinkUrl = callFairconResponse.hostLinkUrl;
      qrCodeInfo.value.guestQrCodeImageUrl = callFairconResponse.guestQrCodeImageUrl;
      qrCodeInfo.value.guestLinkUrl = callFairconResponse.guestLinkUrl;
    };

    const updateUserInfo = (newUserInfo, nickname) => {
      userInfo.value.userType = newUserInfo.userType;
      userInfo.value.isTester = newUserInfo.isTester;
      userInfo.value.nickname = nickname;
    };

    const updateSaveAudio = (newSaveAudio) => {
      saveAudio.value = newSaveAudio;
    };

    const updateDeviceId = (deviceId) => {
      userInfo.value.registeredDeviceId = deviceId;
    };

    const access = async (
      qrCodeType,
      code,
      chatId,
      languageCode,
      givenNickname,
      userType,
      afterSuccess,
      afterFailed,
      logoutCallback = null,
      initializeQrInfo
    ) => {
      const nickname = givenNickname;

      if (QRCodeType.isGuest(qrCodeType) && UserType.isPartner(userType)) {
        handleHostLoginAsGuest(
          qrCodeType,
          code,
          chatId,
          languageCode,
          nickname,
          userType,
          afterSuccess,
          afterFailed,
          logoutCallback
        );
        return;
      }

      if (QRCodeType.isConsultationType(qrCodeType)) {
        // 동시접속 체크
        const amICallingResponse = await amICalling(chatId);
        if (amICallingResponse.data) {
          alertDuplcatedAccess(
            qrCodeType,
            code,
            chatId,
            languageCode,
            nickname,
            userType,
            afterSuccess,
            afterFailed,
            logoutCallback
          );
          return;
        }

        const response = await callConsultation(code, chatId, languageCode);
        if (response.success) {
          init();
        } else {
          afterFailed(response.errorCode);
          return;
        }
      } else {
        // 동시접속 체크
        const amICallingResponse = await amICalling(chatId);
        if (amICallingResponse.data) {
          alertDuplcatedAccess(
            qrCodeType,
            code,
            chatId,
            languageCode,
            nickname,
            userType,
            afterSuccess,
            afterFailed,
            logoutCallback
          );
          return;
        }

        const callFairconResponse = await callFaircon(code, chatId, nickname, languageCode);
        if (callFairconResponse.data === null) {
          afterFailed(callFairconResponse.errorCode);
          return;
        } else {
          if (initializeQrInfo) {
            init();
          }
          updateFairconInfo(callFairconResponse.data);
        }
      }
      afterSuccess();
      return;
    };

    const alertDuplcatedAccess = (
      qrCodeType,
      code,
      chatId,
      languageCode,
      nickname,
      userType,
      afterSuccess,
      afterFailed,
      logoutCallback
    ) => {
      const callbackConfirm = async () => {
        const evictMyselfResponse = await evictMyself();
        if (evictMyselfResponse.data !== null) {
          access(qrCodeType, code, chatId, languageCode, nickname, userType, afterSuccess, afterFailed, logoutCallback);
        } else {
          alertStore.unknownErrorAlert(() => {
            window.location.reload();
          });
        }
      };

      alertEvictMyself(callbackConfirm);
    };

    const handleHostLoginAsGuest = (
      qrCodeType,
      code,
      chatId,
      languageCode,
      givenNickname,
      userType,
      afterSuccess,
      afterFailed,
      logoutCallback
    ) => {
      const nickname = givenNickname;

      const callbackConfirm = () => {
        if (logoutCallback) {
          logoutCallback();
        }
        access(qrCodeType, code, chatId, languageCode, nickname, 'GUEST', afterSuccess, afterFailed, logoutCallback);
      };

      alertLoginGuestAccess(callbackConfirm);
    };

    const amICalling = (chatId) => api.get(`/api/chat/business/call/calling?chatId=${chatId}`);
    const callConsultation = (code, chatId, langCode) =>
      api.post(`/api/chat/business/call/${code}/${chatId}/consultation`, {
        languageCode: langCode,
      });
    const evictMyself = () => api.post(`/api/chat/business/call/evict/myself`);
    const callFaircon = (code, chatId, nickname, langCode) =>
      api.post(`/api/chat/business/call/${code}/${chatId}/faircon`, {
        nickname: nickname,
        languageCode: langCode,
      });

    return {
      qrCodeInfo,
      qrOptions,
      userInfo,
      saveAudio,
      apecChatStatus,
      apecChatStartedAt,
      init,
      updateCode,
      updateFairconInfo,
      updateUserInfo,
      updateSaveAudio,
      updateDeviceId,
      setQrCodeInfo,
      access,
    };
  },
  { persist: true }
); // 상태를 로컬 스토리지에 저장
